/**
 * Partner logo strip (views/home/partners.php) — a continuous slider in
 * "centre mode": the cell nearest the middle of the viewport is brought
 * forward and the rest sit back, so the eye is led along the row.
 *
 * The optical alignment the review asked for comes from the layout rather
 * than from this file: every logo sits in an equal cell, centred and capped
 * to one height (_partners.scss). The set is cloned so the loop never shows
 * a gap; clones are hidden from assistive tech and the tab order.
 *
 * Pauses on hover and keyboard focus, and while the strip is off screen (no
 * frames spent on it then). With prefers-reduced-motion the strip stands
 * still and only the centre mark is emphasised; a focused cell is still
 * shifted into view.
 */

const SPEED = 40; // px per second
const CENTRE_EVERY = 6; // frames between centre recalculations

export function initPartners() {
  document.querySelectorAll('[data-partners]').forEach((viewport) => {
    const list = viewport.querySelector('.hp-partners__list');
    if (!list || list.dataset.slider) {
      return;
    }

    const items = [...list.children];
    if (!items.length) {
      return;
    }
    list.dataset.slider = '1';

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let setWidth = 0;
    let offset = 0;
    let paused = false;
    let frames = 0;
    let raf = 0;
    let visible = true;
    let centre = null;

    const fill = () => {
      list.querySelectorAll('[data-partners-clone]').forEach((clone) => clone.remove());

      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      setWidth = items.reduce((sum, el) => sum + el.getBoundingClientRect().width + gap, 0);

      if (!setWidth || still) {
        return;
      }

      // Enough copies to cover the strip while the first set scrolls out.
      const copies = Math.max(1, Math.ceil(viewport.clientWidth / setWidth) + 1);
      for (let i = 0; i < copies; i++) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.partnersClone = '';
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
          list.append(clone);
        });
      }
    };

    // All reads first, then one class change — and only when the centre
    // moved: removing the class from every cell between reads forced a
    // style recalculation per cell, ten times a second.
    const markCentre = () => {
      const box = viewport.getBoundingClientRect();
      const middle = box.left + box.width / 2;
      let best = null;
      let bestDistance = Infinity;

      for (const el of list.children) {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = el;
        }
      }

      if (best !== centre) {
        centre?.classList.remove('is-center');
        best?.classList.add('is-center');
        centre = best;
      }
    };

    let last = 0;
    const frame = (now) => {
      const delta = last ? (now - last) / 1000 : 0;
      last = now;

      if (!paused && setWidth) {
        offset -= SPEED * delta;
        // One set out of view means the next one is already in its place.
        if (offset <= -setWidth) {
          offset += setWidth;
        }
        list.style.transform = `translate3d(${offset}px, 0, 0)`;
      }

      if (frames++ % CENTRE_EVERY === 0) {
        markCentre();
      }

      raf = visible && !document.hidden ? requestAnimationFrame(frame) : 0;
    };

    const start = () => {
      if (!raf && !still && visible && !document.hidden) {
        last = 0; // a resumed strip must not jump by the stopped time
        raf = requestAnimationFrame(frame);
      }
    };

    const hold = (state) => () => {
      paused = state;
      last = 0; // a resumed strip must not jump by the paused time
    };

    // Pausing alone is not enough for keyboard use: it stops the strip
    // wherever it happens to be, and a focused cell was measured sitting at
    // x = -173, outside the viewport. So a focused cell is also pulled back
    // into view, clamped at the left end of the set. The focused cell grows
    // (--partners-lift, _partners.scss), so an end cell is pulled in by its
    // growth — at the plain edge the overflow clipped 13px of it.
    const reveal = (event) => {
      const item = event.target.closest('.hp-partners__item');
      if (!item || !setWidth) {
        return;
      }
      const lift = parseFloat(getComputedStyle(item).getPropertyValue('--partners-lift')) || 1;
      const grow = (item.offsetWidth * (lift - 1)) / 2;
      offset = Math.min(grow, -(item.offsetLeft + item.offsetWidth / 2 - viewport.clientWidth / 2));
      if (still) {
        // No clones behind it: stop where the last real cell meets the edge.
        offset = Math.max(Math.min(0, viewport.clientWidth - list.scrollWidth) - grow, offset);
      }
      list.style.transform = `translate3d(${offset}px, 0, 0)`;
      markCentre();
    };

    viewport.addEventListener('mouseenter', hold(true));
    viewport.addEventListener('mouseleave', hold(false));
    viewport.addEventListener('focusin', (event) => { hold(true)(); reveal(event); });
    viewport.addEventListener('focusout', hold(false));

    fill();
    markCentre();

    start();

    // Off screen, the loop stops; it resumes where it was when scrolled back.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) start();
      }).observe(viewport);
    }

    let width = viewport.clientWidth;
    const onResize = () => {
      if (viewport.clientWidth === width) {
        return;
      }
      width = viewport.clientWidth;
      offset = 0;
      list.style.transform = '';
      fill();
      markCentre();
    };
    // ResizeObserver arrived in Safari 13.1; older ones fall back to window resizes.
    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(viewport);
    else window.addEventListener('resize', onResize, { passive: true });

    document.addEventListener('visibilitychange', () => {
      // A hidden tab stops firing frames; the loop ends itself and restarts here.
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
      } else {
        start();
      }
    });
  });
}
